import type { MeResponse } from "../api/types.js";

/**
 * Client-firm staff — what the console should do with the signed-in person, decided from `/me`.
 *
 * - `console`: render the console (every AR Dept operator, every platform admin, and anyone whose
 *   `/me` carries no `staff_surface`).
 * - `portal`: this person works in the client portal — show one line and send them to `url`.
 * - `portal-unconfigured`: they work in the portal but the engine has no portal address to give;
 *   show the line without a redirect rather than rendering screens the engine would refuse.
 *
 * A platform admin is never sent away, whatever `/me` says. Pure, so it is tested without a DOM.
 */
export type StaffSurfaceDecision =
  | { kind: "console" }
  | { kind: "portal"; url: string }
  | { kind: "portal-unconfigured" };

export function staffSurfaceDecision(
  me: Pick<MeResponse, "is_platform_admin" | "staff_surface" | "portal_url">,
): StaffSurfaceDecision {
  if (me.is_platform_admin || me.staff_surface !== "portal") return { kind: "console" };
  const url = me.portal_url?.trim();
  if (!url || !/^https?:\/\//i.test(url)) return { kind: "portal-unconfigured" };
  return { kind: "portal", url };
}

/** The one line the person sees while they are redirected. */
export const PORTAL_REDIRECT_MESSAGE =
  "Your firm uses the client portal, not the operator console. Taking you there now…";

/** The line shown when the portal's address is not configured on this deployment. */
export const PORTAL_UNCONFIGURED_MESSAGE =
  "Your firm uses the client portal, not the operator console. Please sign in to your client portal.";
